import { Component, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';

import {
  ChangesPoliciesAbsractComponent
} from '@app/modules/shared/policies-detail/abstracts/changes-policies.abstract';
import { TranslocoService } from '@jsverse/transloco';
import { CurrencyFormatPipe } from '@app/pipes/currency-format.pipe';
import { environment as ENV } from '@environment';
import { AirValidationCondition } from '@app/entities/air-validation.response';

@Component({
  selector: 'app-cancellation-policies',
  templateUrl: './cancellation-policies.component.html',
  styleUrls: ['../changes-policies.abstract.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CancellationPoliciesComponent extends ChangesPoliciesAbsractComponent {

  private readonly DOT: string = '.';
  private readonly COMMA: string = ',';

  constructor(public translateService: TranslocoService,
    public currencyFormat: CurrencyFormatPipe) {
    super(translateService, currencyFormat);
  }

  public buildLabelForCancellation(): string {

    const labelCancellationBefore = this.flight.CancellationAllowed ?
      this.buildSingleLabelForCancellationAllowed(this.flight, false, true)
      : this.buildLabelCancellationNotAllowed(true);
    const detailedLabelCancellationBefore = `${this.labelForChangesBefore} ${labelCancellationBefore}`;

    const labelCancellationAfter = this.flight.CancellationAllowedAfter ?
      this.buildSingleLabelForCancellationAllowed(this.flight, true, true)
      : this.buildLabelCancellationNotAllowed(true);
    const detailedLabelCancellationAfter = `${this.laberForChangesAfter} ${labelCancellationAfter}`;

    const buildFullMessage = (): string => {
      const freeCancellationText = `${this.labelFreeSameDayPurchase}${this.DOT}`;
      const beforeAfterMessages = `${detailedLabelCancellationBefore}${this.DOT} ${detailedLabelCancellationAfter}`;
      return this.isfreeCancellation ?
        `${freeCancellationText} ${this.capitalize(beforeAfterMessages)}`
        : beforeAfterMessages;
    };

    const labelForCancellation = this.hasSameFare ?
      this.buildSingleLabelForCancellationAllowed(this.flight, false, false) : buildFullMessage();

    return !this.hasCancellationAllowed ?
      this.buildLabelCancellationNotAllowed(false) : labelForCancellation;
  }

  get hasCancellationAllowed(): boolean {
    return this.flight?.CancellationAllowed || this.flight?.CancellationAllowedAfter;
  }

  get hasSameFare(): boolean {
    return this.flight?.CancellationAmount?.Fare === this.flight?.CancellationAmountAfter?.Fare
      && this.flight?.CancellationAmount?.Taxes === this.flight?.CancellationAmountAfter?.Taxes;
  }

  private buildSingleLabelForCancellationAllowed(flight: AirValidationCondition,
    isAfter: boolean, isSplitMessage: boolean): string {

    let fare: number = null;
    let currencyCode: string = null;

    if (isAfter) {
      fare = flight?.CancellationAmountAfter?.Fare;
      currencyCode = flight?.CancellationAmountAfter?.CurrencyCode;
      return this.hasCancellationAmountPriceAfter(flight) ?
        this.buildLabelCancellationWithFares(fare, currencyCode, isSplitMessage)
        : this.buildLabelCancellationWithoutFares(isSplitMessage);
    } else {
      fare = flight?.CancellationAmount?.Fare;
      currencyCode = flight?.CancellationAmount?.CurrencyCode;
      return this.hasCancellationAmountPrice(flight) ?
        this.buildLabelCancellationWithFares(fare, currencyCode, isSplitMessage)
        : this.buildLabelCancellationWithoutFares(isSplitMessage);
    }

  }

  private buildLabelCancellationWithFares(fare: number,
    currencyCode: string, isSplitMessage: boolean): string {

    const AIRLINE_CHARGES_CANCELLATION = 'BOOKING_MODULE.CHANGE_CANCEL_POLICIES.AIRLINE_CHARGES_CANCELLATION';

    const airlineChargesCancellation = this.translateService.translate(AIRLINE_CHARGES_CANCELLATION);
    const airlineChargesAmountMessage = (): string =>
      `${this.labelWithFares} (${super.buildLabelFareAmount(fare, currencyCode)})`;

    const fareAmount = () => `(${super.buildLabelFareAmount(fare, currencyCode)})`;
    const fareDetail = () => `${airlineChargesCancellation} ${fareAmount()}`;
    const cancellationAmountMessage = (): string =>
      `${this.labelFreeSameDayPurchase}${this.COMMA} ${fareDetail()}`;

    return this.isfreeCancellation && !isSplitMessage ?
      cancellationAmountMessage()
      : airlineChargesAmountMessage();
  }

  private buildLabelCancellationWithoutFares(isSplitMessage: boolean): string {
    const NO_CHARGE_FARE_CANCELLATION = 'BOOKING_MODULE.CHANGE_CANCEL_POLICIES.NO_CHARGE_FARE_CANCELLATION';
    const FREE_CANCELLATION_RESTRICTIONS = 'BOOKING_MODULE.CHANGE_CANCEL_POLICIES.FREE_CANCELLATION_RESTRICTIONS';

    const noChangeFareCancellation = this.translateService.translate(NO_CHARGE_FARE_CANCELLATION);
    const isfreeCancellationMessage = `${this.labelFreeSameDayPurchase}${this.COMMA} ${noChangeFareCancellation}`;
    return this.isfreeCancellation && !isSplitMessage ? isfreeCancellationMessage
      : this.translateService.translate(FREE_CANCELLATION_RESTRICTIONS);
  }

  private hasCancellationAmountPriceAfter(flight: AirValidationCondition): boolean {
    const { Fare, Taxes } = flight?.CancellationAmountAfter || {};
    return (flight?.CancellationAllowedAfter && (Fare > 0 || Taxes > 0));
  }

  private hasCancellationAmountPrice(flight: AirValidationCondition): boolean {
    const { Fare, Taxes } = flight?.CancellationAmount || {};
    return (flight?.CancellationAllowed && (Fare > 0 || Taxes > 0));
  }

  public buildLabelCancellationNotAllowed(isSplitMessage: boolean): string {
    const DO_NOT_ALLOW_CANCEL_CANCELLATION = 'BOOKING_MODULE.CHANGE_CANCEL_POLICIES.DO_NOT_ALLOW_CANCEL_CANCELLATION';
    const labelNotAllowedAfterPurchaseDay = this.translateService.translate(DO_NOT_ALLOW_CANCEL_CANCELLATION);

    return this.isfreeCancellation && !isSplitMessage ?
      `${this.labelFreeSameDayPurchase}${this.COMMA} ${labelNotAllowedAfterPurchaseDay}`
      : this.labelNotAllowedCancellation;
  }

  get labelNotAllowedCancellation(): string {
    const DO_NOT_ALLOW_CANCEL = 'BOOKING_MODULE.CHANGE_CANCEL_POLICIES.DO_NOT_ALLOW_CANCEL';
    return this.translateService.translate(DO_NOT_ALLOW_CANCEL);
  }

  get labelFreeSameDayPurchase(): string {
    const FREE_CANCELLATION = 'BOOKING_MODULE.CHANGE_CANCEL_POLICIES.FREE_CANCELLATION';
    return this.translateService.translate(FREE_CANCELLATION);
  }

  get isfreeCancellation(): boolean {
    return ENV.countryModules?.cancellation?.flight_enabled;
  }

}
